import React, { useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  InputNumber,
  Switch,
  Typography,
} from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import Flex from 'components/shared-components/Flex';
import NodeSelect from '../NodeSelect';
import {
  intentsConfigToList,
  intentsListToConfig,
  intentsFromEdges,
} from '../../utils/routeByVar';

const { TextArea } = Input;
const { Text } = Typography;


export function AiClassifyConfig({
  config,
  onChange,
  varOptions = [],
  nodeId = null,
  definition = null,
}) {
  const sourceVar = (config.source_var || 'last_message').trim() || 'last_message';
  const outputVar = (config.output_var || 'intent').trim() || 'intent';

  const intents = useMemo(() => intentsConfigToList(config.intents), [config.intents]);

  useEffect(() => {
    if (!nodeId || !definition || intents.length > 0) return;
    const fromEdges = intentsFromEdges(definition, nodeId);
    if (fromEdges.length > 0) {
      onChange({ intents: intentsListToConfig(fromEdges) });
    }
  }, [nodeId, definition]); // eslint-disable-line react-hooks/exhaustive-deps

  const setIntents = (next) => onChange({ intents: intentsListToConfig(next) });

  const updateIntent = (index, patch) => {
    setIntents(intents.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addIntent = () => {
    setIntents([...intents, { label: '', description: '', target_node_id: '' }]);
  };

  const removeIntent = (index) => {
    setIntents(intents.filter((_, i) => i !== index));
  };

  const varSelectOptions =
    sourceVar && !varOptions.some((o) => o.value === sourceVar)
      ? [{ value: sourceVar, label: sourceVar }, ...varOptions]
      : varOptions;

  return (
    <>

      <Form.Item label="Classify this variable" extra="Usually last_message or captured button text.">
        {varOptions.length > 0 ? (
          <Select
            showSearch
            value={sourceVar}
            onChange={(val) => onChange({ source_var: val ?? 'last_message' })}
            options={varSelectOptions}
          />
        ) : (
          <Input
            value={sourceVar}
            onChange={(e) => onChange({ source_var: e.target.value })}
          />
        )}
      </Form.Item>

      <Form.Item label="Instructions" extra="Optional context for the classifier.">
        <TextArea
          rows={3}
          value={config.instructions || ''}
          onChange={(e) => onChange({ instructions: e.target.value })}
          placeholder="e.g. Route the customer based on whether they want more help or are done."
        />
      </Form.Item>

      <Form.Item label="Output variable" extra="Intent label is stored here, then used for routing.">
        <Input
          value={outputVar}
          onChange={(e) => onChange({ output_var: e.target.value })}
        />
      </Form.Item>

      <Form.Item label="Confidence threshold">
        <InputNumber
          min={0}
          max={1}
          step={0.05}
          style={{ width: '100%' }}
          value={config.confidence_threshold ?? 0.7}
          onChange={(val) => onChange({ confidence_threshold: val ?? 0.7 })}
        />
      </Form.Item>

      <Form.Item
        label="Intent routes"
        extra={
          <>
            Each intent label becomes a route branch. Click <strong>Save node</strong> to wire edges.
          </>
        }
      >
        {intents.map((intent, i) => (
          <div
            key={`intent-${i}`}
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: '8px 12px',
              marginBottom: 8,
            }}
          >
            <Flex alignItems="center" gap={8} style={{ gap: 8, marginBottom: 8 }}>
              <Text strong style={{ flex: 1 }}>
                Intent {i + 1}
              </Text>
              <Button type="text" danger size="small" onClick={() => removeIntent(i)}>
                Remove
              </Button>
            </Flex>
            <Form.Item label="Label" style={{ marginBottom: 8 }}>
              <Input
                placeholder="e.g. wants_more_help"
                value={intent.label}
                onChange={(e) => updateIntent(i, { label: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Description (optional)" style={{ marginBottom: 8 }}>
              <Input
                value={intent.description}
                onChange={(e) => updateIntent(i, { description: e.target.value })}
              />
            </Form.Item>
            <Flex alignItems="center" gap={8} style={{ gap: 8, marginBottom: 4 }}>
              <ArrowRightOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
              <Text type="secondary">Send this step</Text>
            </Flex>
            <NodeSelect
              placeholder="Select next step"
              value={intent.target_node_id}
              onChange={(target_node_id) =>
                updateIntent(i, { target_node_id: target_node_id || '' })
              }
            />
          </div>
        ))}
        <Button type="dashed" block onClick={addIntent}>
          Add intent
        </Button>
      </Form.Item>
    </>
  );
}

export function AiExtractConfig({ config, onChange, varOptions = [] }) {
  const fields = Array.isArray(config.fields) ? config.fields : [];
  const sourceVar = (config.source_var || 'last_message').trim() || 'last_message';

  const setFields = (next) => onChange({ fields: next });

  const updateField = (index, patch) => {
    setFields(fields.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  return (
    <>

      <Form.Item label="Extract from variable">
        {varOptions.length > 0 ? (
          <Select
            value={sourceVar}
            onChange={(val) => onChange({ source_var: val ?? 'last_message' })}
            options={varOptions}
          />
        ) : (
          <Input
            value={sourceVar}
            onChange={(e) => onChange({ source_var: e.target.value })}
          />
        )}
      </Form.Item>

      <Form.Item label="Fields to extract">
        {fields.map((field, i) => (
          <div
            key={`field-${i}`}
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: '8px 12px',
              marginBottom: 8,
            }}
          >
            <Flex alignItems="center" gap={8} style={{ gap: 8, marginBottom: 8 }}>
              <Text strong style={{ flex: 1 }}>
                Field {i + 1}
              </Text>
              <Button
                type="text"
                danger
                size="small"
                onClick={() => setFields(fields.filter((_, idx) => idx !== i))}
              >
                Remove
              </Button>
            </Flex>
            <Form.Item label="Name" style={{ marginBottom: 8 }}>
              <Input
                value={field.name || ''}
                onChange={(e) => updateField(i, { name: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Type" style={{ marginBottom: 8 }}>
              <Select
                value={field.type || 'string'}
                onChange={(type) => updateField(i, { type })}
                options={[
                  { value: 'string', label: 'Text' },
                  { value: 'number', label: 'Number' },
                  { value: 'boolean', label: 'Yes/No' },
                  { value: 'date', label: 'Date' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Description" style={{ marginBottom: 8 }}>
              <Input
                value={field.description || ''}
                onChange={(e) => updateField(i, { description: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Required" style={{ marginBottom: 0 }}>
              <Switch
                checked={field.required !== false}
                onChange={(required) => updateField(i, { required })}
              />
            </Form.Item>
          </div>
        ))}
        <Button
          type="dashed"
          block
          onClick={() =>
            setFields([
              ...fields,
              { name: '', type: 'string', description: '', required: true },
            ])
          }
        >
          Add field
        </Button>
      </Form.Item>
    </>
  );
}

export function AiReplyConfig({ config, onChange, varOptions = [] }) {
  return (
    <>
      <Form.Item label="System prompt" required>
        <TextArea
          rows={4}
          value={config.system_prompt || ''}
          onChange={(e) => onChange({ system_prompt: e.target.value })}
          placeholder="You are a helpful support agent for…"
        />
      </Form.Item>

      <Form.Item label="Knowledge variable" extra="Optional session var with extra context.">
        {varOptions.length > 0 ? (
          <Select
            allowClear
            value={config.knowledge_var || undefined}
            onChange={(val) => onChange({ knowledge_var: val ?? '' })}
            options={varOptions}
          />
        ) : (
          <Input
            value={config.knowledge_var || ''}
            onChange={(e) => onChange({ knowledge_var: e.target.value })}
          />
        )}
      </Form.Item>

      <Form.Item label="Knowledge text" extra="Or paste static knowledge (supports {{vars.x}}).">
        <TextArea
          rows={3}
          value={config.knowledge_text || ''}
          onChange={(e) => onChange({ knowledge_text: e.target.value })}
        />
      </Form.Item>

      <Form.Item label="History turns">
        <InputNumber
          min={1}
          max={20}
          style={{ width: '100%' }}
          value={config.history_turns ?? 6}
          onChange={(val) => onChange({ history_turns: val ?? 6 })}
        />
      </Form.Item>

      <Form.Item label="Max tokens">
        <InputNumber
          min={50}
          max={4096}
          style={{ width: '100%' }}
          value={config.max_tokens ?? 1024}
          onChange={(val) => onChange({ max_tokens: val ?? 1024 })}
        />
      </Form.Item>

      <Form.Item label="Handoff when uncertain">
        <Switch
          checked={config.handoff_on_uncertain === true}
          onChange={(handoff_on_uncertain) => onChange({ handoff_on_uncertain })}
        />
      </Form.Item>

      <Form.Item label="End keywords" extra="Comma-separated words that end the AI loop.">
        <Input
          value={(config.end_keywords || []).join(', ')}
          onChange={(e) =>
            onChange({
              end_keywords: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          placeholder="stop, end, agent"
        />
      </Form.Item>
    </>
  );
}

export function AiGenerateConfig({ config, onChange, varOptions = [] }) {
  const sendInline = config.send_inline === true;

  return (
    <>
      <Form.Item label="Prompt" required>
        <TextArea
          rows={4}
          value={config.prompt || ''}
          onChange={(e) => onChange({ prompt: e.target.value })}
          placeholder="Write a friendly reply using {{vars.name}}…"
        />
      </Form.Item>

      <Form.Item label="Send as message (inline)">
        <Switch
          checked={sendInline}
          onChange={(send_inline) => onChange({ send_inline })}
        />
      </Form.Item>

      {!sendInline && (
        <Form.Item label="Output variable">
          {varOptions.length > 0 ? (
            <Select
              showSearch
              allowClear
              value={config.output_var || undefined}
              onChange={(val) => onChange({ output_var: val ?? '' })}
              options={varOptions}
            />
          ) : (
            <Input
              value={config.output_var || ''}
              onChange={(e) => onChange({ output_var: e.target.value })}
            />
          )}
        </Form.Item>
      )}

      <Form.Item label="Temperature">
        <InputNumber
          min={0}
          max={2}
          step={0.1}
          style={{ width: '100%' }}
          value={config.temperature ?? 0.7}
          onChange={(val) => onChange({ temperature: val ?? 0.7 })}
        />
      </Form.Item>
    </>
  );
}
